/**
 * Общий редактор для BankReceipt и BankPayment — структура и логика
 * почти идентичные, отличаются типы операций и направление платежа.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  bankReceipts, bankPayments,
  type BankReceiptCreate, type BankPaymentCreate,
  type ReceiptOperationType, type PaymentOperationType,
} from '@/api/bankDocs';
import { orgs, contractors as contractorsApi } from '@/api/catalogs';
import { employees as employeesApi } from '@/api/hr';
import { refs } from '@/api/refs';
import { useConfirm } from '@/components/ConfirmProvider';
import DatePopover from '@/components/DatePopover';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import ProjectSelect from '@/components/ProjectSelect';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';

interface Props {
  kind: 'receipt' | 'payment';
}

const RECEIPT_OPS: ReceiptOperationType[] = [
  'FROM_BUYER', 'FROM_BANK', 'OTHER', 'LOAN', 'CARD_RECEIPT',
];
const PAYMENT_OPS: PaymentOperationType[] = [
  'TO_SUPPLIER', 'TO_BANK', 'TAX', 'SALARY', 'LOAN_RETURN', 'OTHER',
  'CARD_PAYMENT',
];

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  bank_account_id: string;
  operation_type: string;
  contractor_id: string;
  contract_id: string;
  employee_id: string;  // для SALARY — резолв індивід. рах. розрахунків
  amount: string;
  payment_purpose: string;
  comment: string;
  project_id: string | null;
}

const EMPTY: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  bank_account_id: '',
  operation_type: '',
  contractor_id: '',
  contract_id: '',
  employee_id: '',
  amount: '0',
  payment_purpose: '',
  comment: '',
  project_id: null,
};

export default function BankDocEditor({ kind }: Props) {
  const confirm = useConfirm();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  // обе API одинаковой формы; даём типу свободу пройти через мутации
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const api: any = kind === 'receipt' ? bankReceipts : bankPayments;
  const listKey = kind === 'receipt' ? 'bank-receipts' : 'bank-payments';
  const itemKey = kind === 'receipt' ? 'bank-receipt' : 'bank-payment';
  const baseRoute = kind === 'receipt' ? '/bank-receipts' : '/bank-payments';
  const opOptions = kind === 'receipt' ? RECEIPT_OPS : PAYMENT_OPS;
  const defaultOp = kind === 'receipt' ? 'FROM_BUYER' : 'TO_SUPPLIER';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  // UX α: фільтр org dropdown по bank.payment.manage permission (єдиний
  // для receipt + payment).
  const {
    availableOrgs,
    hasPermissionInAnyOrg,
    totalAccessibleOrgs,
    loading: permsLoading,
  } = usePermissionsByOrg('bank.payment.manage');
  const cList = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.list({ limit: 500 }),
  });
  const docQuery = useQuery({
    queryKey: [itemKey, id],
    queryFn: () => api.get(id!),
    enabled: !isNew,
  });

  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  const baQ = useQuery({
    queryKey: ['bank-accounts', form.organization_id],
    queryFn: () => refs.bankAccounts(form.organization_id),
    enabled: !!form.organization_id,
  });
  const contractsQ = useQuery({
    queryKey: ['contracts', form.organization_id, form.contractor_id],
    queryFn: () => refs.contracts({
      organization_id: form.organization_id,
      contractor_id: form.contractor_id,
    }),
    enabled: !!form.organization_id && !!form.contractor_id,
  });
  // Виплата ЗП (SALARY) — вибір працівника (резолв індивід. рах. розрахунків).
  const showEmployee = kind === 'payment' && form.operation_type === 'SALARY';
  const empQ = useQuery({
    queryKey: ['bank-employees', form.organization_id],
    queryFn: () => employeesApi.list({
      organization_id: form.organization_id, active_only: true, limit: 500,
    }),
    enabled: !!form.organization_id && showEmployee,
  });

  useEffect(() => {
    if (isNew) {
      const firstOrg = availableOrgs[0]?.id ?? '';
      setForm((f) => f.organization_id ? f : {
        ...f,
        organization_id: firstOrg,
        operation_type: f.operation_type || defaultOp,
      });
    } else if (docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        bank_account_id: d.bank_account_id,
        operation_type: d.operation_type,
        contractor_id: d.contractor_id ?? '',
        contract_id: d.contract_id ?? '',
        employee_id: ('employee_id' in d ? d.employee_id : null) ?? '',
        amount: String(d.amount),
        payment_purpose: d.payment_purpose ?? '',
        comment: d.comment ?? '',
        project_id: d.project_id ?? null,
      });
    }
  }, [isNew, docQuery.data, availableOrgs, defaultOp]);

  // подставить главный счёт по умолчанию
  useEffect(() => {
    if (isNew && !form.bank_account_id && baQ.data && baQ.data.length > 0) {
      const main = baQ.data.find((b) => b.is_main) ?? baQ.data[0];
      if (main) setForm((f) => ({ ...f, bank_account_id: main.id }));
    }
  }, [isNew, baQ.data, form.bank_account_id]);

  const isPosted = !isNew && (docQuery.data?.posted ?? false);

  const buildPayload = () => {
    const base = {
      organization_id: form.organization_id,
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      bank_account_id: form.bank_account_id,
      operation_type: form.operation_type as ReceiptOperationType & PaymentOperationType,
      contractor_id: form.contractor_id || null,
      contract_id: form.contract_id || null,
      amount: form.amount,
      payment_purpose: form.payment_purpose || null,
      comment: form.comment || null,
      project_id: form.project_id,
    };
    // employee_id — лише для виплати (BankPayment має це поле; BankReceipt — ні).
    if (kind === 'payment') {
      return { ...base, employee_id: form.employee_id || null };
    }
    return base;
  };

  const create = useMutation({
    mutationFn: () => api.create(buildPayload() as BankReceiptCreate & BankPaymentCreate),
    onSuccess: (doc: { id: string }) => {
      qc.invalidateQueries({ queryKey: [listKey] });
      navigate(`${baseRoute}/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => api.update(id!, buildPayload()),
    onSuccess: () => qc.invalidateQueries({ queryKey: [itemKey, id] }),
    onError: (e) => setError(extractError(e)),
  });
  const post = useMutation({
    mutationFn: () => api.post(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [itemKey, id] });
      qc.invalidateQueries({ queryKey: [listKey] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => api.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [itemKey, id] });
      qc.invalidateQueries({ queryKey: [listKey] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => api.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [listKey] });
      navigate(baseRoute);
    },
  });

  const titleNew = kind === 'receipt' ? t('br.new') : t('bp.new');
  const titleList = kind === 'receipt' ? t('br.title') : t('bp.title');
  const counterpartyLabel = kind === 'receipt' ? t('br.payer') : t('bp.recipient');

  // UX α: warning якщо новий документ а user не має permission у жодній org
  if (isNew && !permsLoading && !hasPermissionInAnyOrg) {
    const what = kind === 'receipt' ? 'платіжку вхідну' : 'платіжку вихідну';
    return (
      <div className="card">
        <div style={{ padding: '1.5rem', textAlign: 'center' }}>
          <h2 style={{ marginTop: 0 }}>{titleNew}</h2>
          <div style={{
            padding: '1rem', background: '#fef3c7', borderRadius: 6,
            border: '1px solid #fbbf24', color: '#92400e',
            maxWidth: 600, margin: '1rem auto',
          }}>
            ⚠ У вас немає прав створювати {what} у жодній з ваших організацій
            ({totalAccessibleOrgs}). Зверніться до адміністратора tenant'а.
          </div>
          <button onClick={() => navigate(baseRoute)}>{t('common.cancel')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? titleNew : `${titleList}: ${form.document_number}`}
          {isPosted && <span style={{ marginLeft: '0.75rem', color: '#15803d', fontSize: '1rem' }}>✓ {t('mo.posted_yes')}</span>}
        </h2>
        <DocumentToolbar
          onBackToList={() => navigate(baseRoute)}
          onSave={() => isNew ? create.mutate() : update.mutate()}
          saveDisabled={create.isPending || update.isPending}
          isPosted={isPosted}
          isNew={isNew}
          onPost={() => post.mutate()}
          postPending={post.isPending}
          postDisabled={!(Number(form.amount) > 0)}
          onUnpost={() => unpost.mutate()}
          unpostPending={unpost.isPending}
          onDelete={() => { confirm(t('common.confirm_delete')).then((ok) => { if (ok) remove.mutate(); }); }}
          deletePending={remove.isPending}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <label>{t('bd.organization')}</label>
          <select value={form.organization_id}
                  onChange={(e) => setForm({ ...form, organization_id: e.target.value, bank_account_id: '' })}
                  disabled={!isNew}>
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: '#64748b', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
              (інші не дозволяють цю операцію)
            </small>
          )}
        </div>
        <div className="field">
          <label>{t('bd.number')}</label>
          <input value={form.document_number}
                 onChange={(e) => setForm({ ...form, document_number: e.target.value })}
                 disabled={isPosted} />
        </div>
        <div className="field">
          <label>{t('bd.date')}</label>
          <DatePopover withTime value={form.document_date}
                 onChange={(v) => setForm({ ...form, document_date: v })}
                 disabled={isPosted} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <label>{t('bd.bank_account')}</label>
          <select value={form.bank_account_id}
                  onChange={(e) => setForm({ ...form, bank_account_id: e.target.value })}
                  disabled={isPosted}>
            <option value="">—</option>
            {baQ.data?.map((b) => (
              <option key={b.id} value={b.id}>{b.iban} {b.account_name ? '— ' + b.account_name : ''}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>{t('bd.operation_type')}</label>
          <select value={form.operation_type}
                  onChange={(e) => setForm({ ...form, operation_type: e.target.value })}
                  disabled={isPosted}>
            {opOptions.map((op) => (
              <option key={op} value={op}>{t(`bd.ops.${op}`)}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>{t('bd.amount')}</label>
          <input type="number" step="0.01" value={form.amount}
                 onChange={(e) => setForm({ ...form, amount: e.target.value })}
                 disabled={isPosted} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <label>{counterpartyLabel}</label>
          <select value={form.contractor_id}
                  onChange={(e) => setForm({ ...form, contractor_id: e.target.value, contract_id: '' })}
                  disabled={isPosted}>
            <option value="">—</option>
            {cList.data?.items.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>{t('bd.contract')}</label>
          <select value={form.contract_id}
                  onChange={(e) => setForm({ ...form, contract_id: e.target.value })}
                  disabled={isPosted || !form.contractor_id}>
            <option value="">—</option>
            {contractsQ.data?.map((c) => (
              <option key={c.id} value={c.id}>{c.code} — {c.name}</option>
            ))}
          </select>
        </div>
        {showEmployee && (
          <div className="field">
            <label>Працівник</label>
            <select value={form.employee_id}
                    onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
                    disabled={isPosted}
                    title="Для виплати ЗП — індивідуальний рахунок розрахунків працівника підставиться у проводку (правило/картка/661).">
              <option value="">—</option>
              {empQ.data?.items.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.full_name ?? e.code ?? e.id}{e.code && e.full_name ? ` (${e.code})` : ''}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="field">
        <label>{t('bd.purpose')}</label>
        <textarea value={form.payment_purpose}
                  onChange={(e) => setForm({ ...form, payment_purpose: e.target.value })}
                  disabled={isPosted}
                  rows={2} />
      </div>

      <div className="field">
        <label>Проект / напрям</label>
        <ProjectSelect
          value={form.project_id}
          organizationId={form.organization_id}
          onChange={(v) => setForm({ ...form, project_id: v })}
          disabled={isPosted} />
      </div>

      {error && <div className="error">{error}</div>}
    </div>
  );
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as {
      response?: {
        data?: { detail?: string | Array<{ loc?: unknown[]; msg?: string }> }
      }
    }).response;
    const detail = r?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      return detail.slice(0, 3).map((item) => {
        const field = Array.isArray(item.loc) && item.loc.length > 1
          ? String(item.loc[item.loc.length - 1]) : 'поле';
        return `${field}: ${item.msg ?? 'невідома помилка'}`;
      }).join('; ');
    }
    return 'Error';
  }
  return String(e);
}
